// filepath: apps/web/src/design-system/DocumentHeader.tsx
//
// FR-UI-CORE-3 -- DocumentHeader primitive ported from the reference
// JSX in docs/guides/design system/components/data/DocumentHeader.jsx.
//
// Contract (apps/web/src/design-system/DocumentHeader.d.ts):
//   - title:        page-level title (required)
//   - subtitle:     optional sub-line
//   - status:       optional status string rendered as a Badge
//   - statusTone:   'neutral' | 'success' | 'danger' | 'warning' | 'primary'
//   - fields:       optional DocumentHeaderField[]
//   - actions:      optional ReactNode (rendered right-aligned)
//
// Tokens via var(--semantic-alias) only -- no raw hex.

import React from "react";
import { Badge } from "./Badge";

export interface DocumentHeaderField {
  label: string;
  value: React.ReactNode;
}

export type DocumentHeaderStatusTone =
  "neutral" | "success" | "danger" | "warning" | "primary";

export interface DocumentHeaderProps {
  title: string;
  subtitle?: string;
  status?: string;
  statusTone?: DocumentHeaderStatusTone;
  fields?: readonly DocumentHeaderField[];
  actions?: React.ReactNode;
  /** Test hook forwarded to the root container. */
  "data-testid"?: string;
}

export function DocumentHeader({
  title,
  subtitle,
  status,
  statusTone = "neutral",
  fields = [],
  actions,
  "data-testid": testId,
}: DocumentHeaderProps): React.ReactElement {
  return (
    <div
      data-testid={testId}
      className="ds-document-header"
      style={{
        padding: "var(--space-6)",
        borderBottom: "1px solid var(--border-default)",
        background: "var(--surface-card)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "16px",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h1
              style={{
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-bold)",
                color: "var(--text-primary)",
                margin: 0,
              }}
            >
              {title}
            </h1>
            {status !== undefined && status.length > 0 ? (
              <Badge tone={statusTone}>{status}</Badge>
            ) : null}
          </div>
          {subtitle !== undefined && subtitle.length > 0 ? (
            <div
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--text-secondary)",
                marginTop: "4px",
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
        {actions !== undefined ? (
          <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
            {actions}
          </div>
        ) : null}
      </div>
      {fields.length > 0 ? (
        <div
          style={{
            display: "flex",
            gap: "32px",
            marginTop: "var(--space-5)",
          }}
        >
          {fields.map((f) => (
            <div key={f.label}>
              <div
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--text-secondary)",
                }}
              >
                {f.label}
              </div>
              <div
                style={{
                  fontSize: "var(--text-md)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--text-primary)",
                  marginTop: "2px",
                }}
              >
                {f.value}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
